import { useEffect, useRef, useState } from "react";
import { tickerNews, site } from "@/mocks/guaData";

const searchIndex = [
  { label: "首页", anchor: "#top", keys: "首页 home 顶部" },
  { label: "热点追踪 / 热搜速览", anchor: "#hot", keys: "热点 热搜 榜 排名 追踪" },
  { label: "热门栏目", anchor: "#channel", keys: "栏目 娱乐 综艺 主播 复盘 辟谣" },
  { label: "事件复盘时间线", anchor: "#timeline", keys: "复盘 时间线 事件 发酵 进展" },
  { label: "深度专题", anchor: "#story", keys: "专题 深度 关系 情绪 媒介 素养" },
  { label: "关于我们", anchor: "#about", keys: "关于 原则 口碑 我们 简介" },
  { label: "常见问题", anchor: "#faq", keys: "常见问题 faq 投稿 收录 疑问" },
  { label: "订阅周报", anchor: "#subscribe", keys: "订阅 周报 邮箱 邮件 newsletter" },
];

function Brand() {
  return (
    <a href="#top" className="group flex items-center gap-2.5">
      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-lg text-white shadow-sm">
        <i className="ri-flashlight-line" />
      </span>
      <span className="flex flex-col leading-none">
        <span className="font-serif text-2xl font-extrabold tracking-tight text-stone-900">
          {site.brand}
        </span>
        <span className="mt-1 text-[10px] tracking-[0.28em] text-stone-500">HOT · TOPIC · DAILY</span>
      </span>
    </a>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
    if (!searchOpen) setQuery("");
  }, [searchOpen]);

  const matched = query.trim()
    ? searchIndex.filter((s) => s.keys.includes(query.trim()) || s.label.includes(query.trim()))
    : [];

  const go = (anchor: string) => {
    setSearchOpen(false);
    setOpen(false);
    setQuery("");
    document.querySelector(anchor)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header id="top" className="sticky top-0 z-50">
      {/* 顶部细条 */}
      <div className="bg-primary-600 text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-1.5 text-xs md:px-6">
          <p className="hidden items-center gap-2 sm:flex">
            <i className="ri-calendar-line" />
            <span>热点事件速报 · 每日更新</span>
          </p>
          <div className="flex items-center gap-3 text-white/85">
            <a href="#subscribe" className="hidden whitespace-nowrap hover:text-white md:inline-flex md:items-center md:gap-1"><i className="ri-mail-line" /> 订阅周报</a>
            <a href="#about" className="whitespace-nowrap hover:text-white">投稿入口</a>
            <span className="hidden whitespace-nowrap sm:inline">| 辟谣求真 · 月度报告</span>
          </div>
        </div>
      </div>

      {/* 主品牌行 */}
      <div
        className={`border-b border-paper-200/80 transition-colors ${
          scrolled ? "bg-paper-50/95 backdrop-blur" : "bg-paper-50"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-6">
          <Brand />
          <p className="hidden max-w-md flex-1 px-6 text-center font-serif text-sm italic text-stone-500 lg:block">
            “{site.slogan}”
          </p>
          <div className="flex items-center gap-2">
            {/* 搜索 */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setSearchOpen((v) => !v)}
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-paper-200 bg-white text-stone-600 transition hover:border-primary-300 hover:text-primary-600"
                aria-label="站内搜索"
              >
                <i className="ri-search-line text-lg" />
              </button>
              {searchOpen && (
                <div className="absolute right-0 top-12 w-72 overflow-hidden rounded-lg border border-paper-200 bg-white shadow-lg">
                  <div className="flex items-center gap-2 border-b border-paper-200 px-3 py-2.5">
                    <i className="ri-search-line text-stone-400" />
                    <input
                      ref={inputRef}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="搜栏目 / 事件关键词…"
                      className="w-full bg-transparent text-sm outline-none placeholder:text-stone-400"
                    />
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    {matched.length === 0 && query.trim() ? (
                      <p className="px-4 py-4 text-sm text-stone-500">未找到相关内容，试试“热点 / 复盘 / 订阅”。</p>
                    ) : (
                      matched.map((s) => (
                        <button
                          key={s.label}
                          type="button"
                          onClick={() => go(s.anchor)}
                          className="flex w-full cursor-pointer items-center gap-2 px-4 py-2.5 text-left text-sm text-stone-700 transition hover:bg-primary-50 hover:text-primary-700"
                        >
                          <i className="ri-corner-down-right-line text-stone-400" />
                          {s.label}
                        </button>
                      ))
                    )}
                    {matched.length === 0 && !query.trim() && (
                      searchIndex.map((s) => (
                        <button
                          key={s.label}
                          type="button"
                          onClick={() => go(s.anchor)}
                          className="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-sm text-stone-600 transition hover:bg-primary-50 hover:text-primary-700"
                        >
                          <i className="ri-link text-stone-400" />
                          {s.label}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <a
              href="#subscribe"
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-primary-600 sm:inline-flex sm:items-center sm:gap-1.5"
            >
              <i className="ri-add-line" />
              订阅
            </a>

            {/* 汉堡 */}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-paper-200 bg-white text-stone-700 md:hidden"
              aria-label="打开菜单"
            >
              <i className={`${open ? "ri-close-line" : "ri-menu-line"} text-lg`} />
            </button>
          </div>
        </div>

        {/* 桌面栏目导航 */}
        <nav className="mx-auto hidden max-w-7xl items-center gap-1 px-4 pb-2 md:flex md:px-6">
          {[
            { label: "热点追踪", href: "#hot" },
            { label: "热门栏目", href: "#channel" },
            { label: "事件复盘", href: "#timeline" },
            { label: "深度专题", href: "#story" },
            { label: "关于我们", href: "#about" },
            { label: "常见问题", href: "#faq" },
          ].map((n) => (
            <a
              key={n.label}
              href={n.href}
              className="whitespace-nowrap rounded-full px-4 py-1.5 text-sm text-stone-600 transition hover:bg-primary-100 hover:text-primary-700"
            >
              {n.label}
            </a>
          ))}
        </nav>
      </div>

      {/* 跑马灯 */}
      <div className="marquee-paused overflow-hidden border-b border-paper-200 bg-white/80">
        <div className="mx-auto flex max-w-7xl items-center px-4 md:px-6">
          <span className="z-10 mr-3 flex shrink-0 items-center gap-1.5 rounded-full bg-accent-500 px-2.5 py-1 text-[11px] font-semibold text-white">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
            快讯
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="animate-marquee flex items-center gap-10 whitespace-nowrap">
              {[...tickerNews, ...tickerNews].map((t, i) => (
                <span key={i} className="flex items-center gap-10 text-sm text-stone-600">
                  <i className="ri-fire-line text-primary-500" />
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 移动端菜单 */}
      {open && (
        <div className="border-t border-paper-200 bg-paper-50 md:hidden">
          <nav className="mx-auto grid max-w-7xl grid-cols-2 gap-1 px-4 py-4">
            {[
              { label: "首页", href: "#top" },
              { label: "热点追踪", href: "#hot" },
              { label: "热门栏目", href: "#channel" },
              { label: "事件复盘", href: "#timeline" },
              { label: "深度专题", href: "#story" },
              { label: "关于我们", href: "#about" },
              { label: "常见问题", href: "#faq" },
              { label: "订阅周报", href: "#subscribe" },
            ].map((n) => (
              <a
                key={n.label}
                href={n.href}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-3 text-sm text-stone-700 transition hover:bg-primary-100 hover:text-primary-700"
              >
                {n.label}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}